import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Logo } from "./Logo";

const LINKS = [
  { to: "/how-it-works", label: "How It Works" },
  { to: "/packages", label: "Packages" },
  { to: "/gallery", label: "Gallery" },
  { to: "/faq", label: "FAQ" },
  { to: "/about", label: "About" },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [loc.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-cream/85 backdrop-blur-md border-b border-clubborder" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 h-[68px] flex items-center justify-between">
        <Link to="/" data-testid="nav-logo-link" aria-label="The Event Club home">
          <Logo />
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
              className={`text-[14px] tracking-wide transition-colors hover:text-forest ${
                loc.pathname === l.to ? "text-forest" : "text-ink/70"
              }`}
            >
              {l.label}
            </Link>
          ))}
          <Link
            to="/book"
            data-testid="nav-book-cta"
            className="rounded-full bg-forest text-cream text-[14px] px-5 py-2.5 hover:bg-forest-deep transition-colors"
          >
            Check Your Date
          </Link>
        </nav>

        <button
          className="md:hidden text-ink p-1"
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden overflow-hidden bg-cream border-b border-clubborder"
          >
            <div className="px-5 py-5 flex flex-col gap-1">
              {LINKS.map((l) => (
                <Link key={l.to} to={l.to} className="py-3 text-lg font-serif border-b border-clubborder/60" data-testid={`mobile-nav-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
                  {l.label}
                </Link>
              ))}
              <Link to="/book" className="mt-4 text-center rounded-full bg-forest text-cream px-5 py-3" data-testid="mobile-nav-book">
                Check Your Date
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
